import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  useCollection, useFilteredDonors, promoteDonorToAdmin, createDonor, 
  logContactAttempt, toggleDonorStatus as toggleDonorStatusInDb, 
  deleteDonor as deleteDonorFromDb, useAdmins, updateDocument,
  getDonorResponsesByRequest
} from '../hooks/useSupabase';
import { isSuperAdmin, hasPermission, getUserRole, ADMIN_ROLES } from '../config/admin';
import { DISTRICTS_KERALA, BLOOD_GROUPS } from '../types/schema';
import AdminManagement from './AdminManagement';
import AdminAnalytics from './AdminAnalytics';
import QuickContactModal from './QuickContactModal';
import DonorList from './DonorList';
import MobileBottomNav from './MobileBottomNav';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';
import DonorRegistration from './DonorRegistration';
import AdminQRGenerator from './AdminQRGenerator';
import BloodRequestForm from './BloodRequestForm';

const AdminDashboardNew = ({ currentUser, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // If not logged in as admin, redirect to home
  useEffect(() => {
    if (!currentUser) navigate('/', { replace: true });
  }, [currentUser, navigate]);
  
  // Data fetching
  const { data: requests, loading: requestsLoading, refresh: refreshRequests } = useCollection('blood_requests', [['created_at', 'order', 'desc']]);

  const { data: admins, refresh: refreshAdmins } = useAdmins();
  const { data: donors, loading: donorsLoading, refresh: refreshDonors } = useFilteredDonors({});
  const { data: donationHistory, refresh: refreshDonationHistory } = useCollection('donation_history', [['donation_date', 'order', 'desc']]);

  // Location-based filtering
  const isLocationRestricted = useMemo(() => {
    return ADMIN_ROLES[currentUser?.role]?.locationRestricted;
  }, [currentUser]);

  const allowedLocations = useMemo(() => {
    return currentUser?.allowed_locations || [];
  }, [currentUser]);

  const accessibleDonors = useMemo(() => {
    if (!donors) return [];
    if (!isLocationRestricted) return donors;
    return donors.filter(d => allowedLocations.includes(d.district));
  }, [donors, isLocationRestricted, allowedLocations]);

  const accessibleRequests = useMemo(() => {
    if (!requests) return [];
    if (!isLocationRestricted) return requests;
    return requests.filter(r => allowedLocations.includes(r.hospital_district || r.hospital?.district));
  }, [requests, isLocationRestricted, allowedLocations]);

  const accessibleDonationHistory = useMemo(() => {
    if (!donationHistory) return [];
    if (!isLocationRestricted) return donationHistory;
    const accessibleDonorIds = new Set(accessibleDonors.map(d => d.id));
    return donationHistory.filter(h => accessibleDonorIds.has(h.donor_id));
  }, [donationHistory, isLocationRestricted, accessibleDonors]);
  
  // Filters
  const [donorFilters, setDonorFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDonors, setSelectedDonors] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Donor responses by request
  const [donorResponses, setDonorResponses] = useState({}); // { [requestId]: responses[] }
  const [expandedRequests, setExpandedRequests] = useState({}); // { [requestId]: boolean }
  
  // Fetch donor responses for all active requests
  useEffect(() => {
    if (requests?.length > 0) {
      requests.forEach(async (req) => {
        if (req.status === 'active') {
          try {
            const responses = await getDonorResponsesByRequest(req.id);
            if (responses?.length > 0) {
              setDonorResponses(prev => ({ ...prev, [req.id]: responses }));
            }
          } catch (err) {
            console.error('Error fetching responses for request', req.id, err);
          }
        }
      });
    }
  }, [requests]);
  
  // Modals
  const [showAddDonorModal, setShowAddDonorModal] = useState(false);
  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showRecordDonationModal, setShowRecordDonationModal] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [donationDate, setDonationDate] = useState('');
  const [promoteRole, setPromoteRole] = useState('DISTRICT_ADMIN');
  const [promoteDistricts, setPromoteDistricts] = useState([]);
  const [promoteLoading, setPromoteLoading] = useState(false);
  
  const { isOpen, config, confirm, handleClose } = useConfirm();
  
  // Get profile display info
  const getProfileInfo = () => {
    const role = currentUser?.role || (isSuperAdminUser ? 'SUPER_ADMIN' : 'ADMIN');
    const roleConfig = ADMIN_ROLES[role];
    return {
      role: roleConfig?.name || role,
      description: roleConfig?.description || '',
      permissions: roleConfig?.permissions.map(p => 
        p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      ) || [],
      locations: currentUser?.allowed_locations || (roleConfig?.locationRestricted ? [] : ['All Locations']),
      isLocationRestricted: roleConfig?.locationRestricted || false
    };
  };
  
  const userRole = getUserRole(currentUser);
  const isSuperAdminUser = isSuperAdmin(currentUser?.email);
  // Allow any admin with a role (SUPER_ADMIN, DISTRICT_ADMIN, UNIT_ADMIN, etc.)
  const isAdmin = !!userRole || isSuperAdminUser || currentUser?.role;
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have admin privileges to access this dashboard.</p>
        </div>
      </div>
    );
  }

  // Stats
  const stats = {
    totalDonors: accessibleDonors?.length || 0,
    availableDonors: accessibleDonors?.filter(d => d.is_available !== false).length || 0,
    activeRequests: accessibleRequests?.filter(r => r.status === 'active').length || 0,
    fulfilledThisMonth: accessibleRequests?.filter(r => {
      const date = new Date(r.fulfilled_at || r.created_at);
      const now = new Date();
      return r.status === 'fulfilled' && date.getMonth() === now.getMonth();
    }).length || 0
  };

  const handleLogContact = async (data) => {
    try {
      await logContactAttempt(data.donorId, {
        method: data.method,
        message: data.message,
        status: data.status
      });
    } catch (err) {
      console.error('Failed to log contact:', err);
    }
  };

  const toggleDonorSelection = (donorId) => {
    setSelectedDonors(prev => 
      prev.includes(donorId) 
        ? prev.filter(id => id !== donorId)
        : [...prev, donorId]
    );
  };

  const toggleDonorStatus = (donor) => {
    const newStatus = donor.is_available === false || donor.isAvailable === false;
    confirm({
      title: newStatus ? 'Activate Donor?' : 'Deactivate Donor?',
      message: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} ${donor.name}?`,
      confirmText: newStatus ? 'Activate' : 'Deactivate',
      type: newStatus ? 'success' : 'warning',
      onConfirm: async () => {
        try {
          await toggleDonorStatusInDb(donor.id, !newStatus);
          toast.success(`Donor ${newStatus ? 'activated' : 'deactivated'}!`);
          refreshDonors();
        } catch (error) {
          toast.error('Failed to update status.');
        }
      }
    });
  };

  const deleteDonor = (donor) => {
    confirm({
      title: 'Delete Donor?',
      message: `Permanently delete ${donor.name}? This cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteDonorFromDb(donor.id);
          toast.success('Donor deleted.');
          refreshDonors();
        } catch (error) {
          toast.error('Failed to delete.');
        }
      }
    });
  };

  const handlePromoteDonor = (donor) => {
    setSelectedDonor(donor);
    setPromoteRole('DISTRICT_ADMIN');
    setPromoteDistricts(donor.district ? [donor.district] : []);
    setShowPromoteModal(true);
  };

  const handleRecordDonation = (donor) => {
    setSelectedDonor(donor);
    setDonationDate(new Date().toISOString().split('T')[0]); // Default to today
    setShowRecordDonationModal(true);
  };

  const submitDonationRecord = async () => {
    if (!selectedDonor || !donationDate) return;
    try {
      // Parse YYYY-MM-DD at noon UTC to avoid timezone boundary shifts
      const [y, m, d] = donationDate.split('-').map(Number);
      const donationDateObj = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
      const nextEligible = new Date(donationDateObj);
      nextEligible.setUTCDate(nextEligible.getUTCDate() + 56);
      await updateDocument('donors', selectedDonor.id, {
        last_donation_date: donationDateObj.toISOString(),
        next_eligible_date: nextEligible.toISOString(),
        total_donations: (selectedDonor.total_donations || 0) + 1,
        updated_at: new Date().toISOString()
      });
      toast.success(`Blood donation recorded for ${selectedDonor.name}! 🩸`);
      setShowRecordDonationModal(false);
      setSelectedDonor(null);
      refreshDonors?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to record donation. Please try again.');
    }
  };

  const handleCreateAdmin = async () => {
    if (!selectedDonor) return;
    const roleConfig = ADMIN_ROLES[promoteRole];
    if (!roleConfig) return;
    if (roleConfig.locationRestricted && promoteDistricts.length === 0) {
      toast.error('Please select at least one district.');
      return;
    }
    setPromoteLoading(true);
    try {
      await promoteDonorToAdmin(selectedDonor.id, {
        role: promoteRole,
        permissions: roleConfig.permissions,
        allowedLocations: roleConfig.locationRestricted ? promoteDistricts : []
      });
      toast.success(`${selectedDonor.name} promoted to ${roleConfig.name}!`);
      setShowPromoteModal(false);
      setSelectedDonor(null);
      refreshAdmins();
    } catch (error) {
      toast.error('Failed to promote donor: ' + (error?.message || 'Unknown error'));
    } finally {
      setPromoteLoading(false);
    }
  };

  const handleFulfillRequest = async (requestId) => {
    confirm({
      title: 'Mark as Fulfilled?',
      message: 'Are you sure you want to mark this blood request as fulfilled? This will close the request.',
      confirmText: 'Yes, Mark as Fulfilled',
      cancelText: 'Cancel',
      type: 'success',
      onConfirm: async () => {
        try {
          await updateDocument('blood_requests', requestId, {
            status: 'fulfilled',
            fulfilled_at: new Date().toISOString(),
            fulfilled_by: currentUser?.id || currentUser?.email || 'unknown'
          });
          toast.success('Request marked as fulfilled! 🎉');
          // Refresh requests to update leaderboard
          refreshRequests?.();
        } catch (error) {
          console.error('Error fulfilling request:', error);
          toast.error('Failed to fulfill request.');
        }
      }
    });
  };

  // Calculate fulfillment leaderboard from requests
  const calculateLeaderboard = () => {
    if (!accessibleRequests) return [];
    
    const fulfillmentMap = {};
    accessibleRequests.forEach(req => {
      if (req.status === 'fulfilled' && req.fulfilled_by) {
        const key = req.fulfilled_by;
        if (!fulfillmentMap[key]) {
          fulfillmentMap[key] = {
            id: key,
            name: key, // Could be email or id
            count: 0,
            lastFulfilled: null
          };
        }
        fulfillmentMap[key].count++;
        const fulfilledDate = new Date(req.fulfilled_at);
        if (!fulfillmentMap[key].lastFulfilled || fulfilledDate > new Date(fulfillmentMap[key].lastFulfilled)) {
          fulfillmentMap[key].lastFulfilled = req.fulfilled_at;
        }
      }
    });
    
    // Sort by count descending
    return Object.values(fulfillmentMap).sort((a, b) => b.count - a.count);
  };

  // Render Overview
  const renderOverview = () => (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Welcome */}
      <div className="rounded-2xl p-6 text-white" style={{background: 'linear-gradient(160deg, #5c3a1e 0%, #8f2a0e 40%, #c4451a 75%, #d4a96a 100%)'}}>
        <h2 className="text-2xl font-bold mb-1">Welcome back! 👋</h2>
        <p style={{color: '#f9efd5', opacity: 0.85}}>{currentUser?.email}</p>
        <div className="mt-4 flex gap-3">
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
            {isSuperAdminUser ? '👑 Super Admin' : '👤 Admin'}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Donors', value: stats.totalDonors, icon: '👥', color: 'bg-blue-500' },
          { label: 'Available', value: stats.availableDonors, icon: '✅', color: 'bg-green-500' },
          { label: 'Active Requests', value: stats.activeRequests, icon: '🩸', color: 'bg-red-500' },
          { label: 'Fulfilled (This Month)', value: stats.fulfilledThisMonth, icon: '🎉', color: 'bg-purple-500' }
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center text-white text-xl mb-3`}>
              {stat.icon}
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'View All Donors', icon: '👥', action: () => setActiveTab('donors'), color: 'bg-blue-50 text-blue-700' },
            { label: 'Blood Requests', icon: '🩸', action: () => setActiveTab('requests'), color: 'bg-amber-50 text-amber-800' },
            { label: 'Add New Donor', icon: '➕', action: () => setShowAddDonorModal(true), color: 'bg-green-50 text-green-700' },
            { label: 'Manage Admins', icon: '⚙️', action: () => setActiveTab('admins'), color: 'bg-purple-50 text-purple-700', hidden: !isSuperAdminUser }
          ].filter(b => !b.hidden).map((btn, i) => (
            <button
              key={i}
              onClick={btn.action}
              className={`p-4 rounded-2xl ${btn.color} font-medium text-sm flex flex-col items-center gap-2 hover:shadow-md transition-all`}
            >
              <span className="text-2xl">{btn.icon}</span>
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Admin Fulfillment Leaderboard */}
      {(() => {
        const leaderboard = calculateLeaderboard();
        if (leaderboard.length === 0) return null;
        
        return (
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-sm border border-yellow-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center text-white text-xl">
                🏆
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Facilitator Leaderboard</h3>
                <p className="text-sm text-gray-600">Blood arrangements completed</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {leaderboard.slice(0, 5).map((admin, index) => (
                <div key={admin.id} className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-yellow-500 text-white' :
                    index === 1 ? 'bg-gray-400 text-white' :
                    index === 2 ? 'bg-orange-400 text-white' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {admin.name === currentUser?.email ? 'You' : admin.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Last: {admin.lastFulfilled ? new Date(admin.lastFulfilled).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{color: '#f2ddb0'}}>{admin.count}</p>
                    <p className="text-xs text-gray-500">arrangements</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Recent Blood Requests</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {accessibleRequests?.slice(0, 5).map((req) => (
            <div key={req.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: '#fdf9f0', border: '1px solid #c8924a'}}>
                  <span className="font-bold" style={{color: '#8f2a0e'}}>{req.blood_group}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{req.patient_name || 'Patient'}</p>
                  <p className="text-sm text-gray-500">{req.hospital?.name || req.hospital_name} • {req.hospital?.location || req.hospital_location}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                req.status === 'active' ? 'bg-red-100 text-red-700' :
                req.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {req.status}
              </span>
            </div>
          ))}
          {!accessibleRequests?.length && (
            <p className="p-8 text-center text-gray-500">No blood requests yet</p>
          )}
        </div>
      </div>
    </div>
  );

  // Export donors to CSV
  const exportDonors = () => {
    if (!accessibleDonors?.length) {
      toast.error('No donors to export');
      return;
    }
    
    // CSV headers
    const headers = ['Name', 'Blood Group', 'Phone', 'Email', 'District', 'Location', 'Status', 'Last Donation'];
    
    // CSV rows
    const rows = accessibleDonors.map(d => [
      d.name,
      d.blood_group,
      d.phone_number,
      d.email || '',
      d.district,
      d.location,
      d.is_available !== false ? 'Available' : 'Unavailable',
      d.last_donation_date ? new Date(d.last_donation_date).toLocaleDateString() : 'Never'
    ]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `donors-backup-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${accessibleDonors.length} donors to CSV`);
  };

  // Render Donors - using optimized DonorList component
  const renderDonors = () => (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 160px)' }}>
      {/* Super Admin Export Button */}
      {isSuperAdminUser && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={exportDonors}
            className="px-4 py-2 bg-gray-800 text-white rounded-xl font-medium hover:bg-gray-900 flex items-center gap-2 text-sm"
          >
            <span>📥</span> Export All Donors (CSV)
          </button>
        </div>
      )}
      
      <DonorList
        donors={accessibleDonors}
        admins={admins}
        isLoading={donorsLoading}
        selectedDonors={selectedDonors}
        onToggleSelection={toggleDonorSelection}
        onContact={() => {}}
        onToggleStatus={toggleDonorStatus}
        onPromote={handlePromoteDonor}
        onDelete={deleteDonor}
        onLogContact={handleLogContact}
        onRecordDonation={handleRecordDonation}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={donorFilters}
        onFilterChange={(key, value) => setDonorFilters(prev => ({ ...prev, [key]: value }))}
        request={selectedRequest}
      />
    </div>
  );

  // Render Requests
  const renderRequests = () => (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Blood Requests</h2>
        <button 
          onClick={() => setShowAddRequestModal(true)}
          className="px-4 py-2 text-white rounded-xl font-medium" 
          style={{background: '#c4451a'}}
        >
          ➕ New Request
        </button>
      </div>

      <div className="grid gap-4">
        {accessibleRequests?.map((req) => (
          <div key={req.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{background: '#fdf9f0', border: '2px solid #c8924a'}}>
                  <span className="text-xl font-bold" style={{color: '#8f2a0e'}}>{req.blood_group}</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{req.patient_name || 'Patient'}</h3>
                  <p className="text-sm text-gray-500">{req.hospital?.name || req.hospital_name}</p>
                  <p className="text-xs text-gray-400">{req.hospital?.location || req.hospital_location}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                req.status === 'active' ? 'bg-red-100 text-red-700' :
                req.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {req.status}
              </span>
            </div>
            
            <div className="flex gap-2 text-sm text-gray-600 mb-4">
              <span className="bg-gray-100 px-2 py-1 rounded">🩸 {req.units_required || 1} units</span>
              <span className="bg-gray-100 px-2 py-1 rounded">📅 {req.required_by || 'ASAP'}</span>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleFulfillRequest(req.id)}
                className="flex-1 py-2 bg-green-50 text-green-700 rounded-xl font-medium hover:bg-green-100"
              >
                ✅ Mark Fulfilled
              </button>
              <button 
                onClick={() => {
                  setSelectedRequest(req);
                  setDonorFilters({ bloodGroup: req.blood_group });
                  setActiveTab('donors');
                  toast.success(`Finding ${req.blood_group} donors for this request`);
                }}
                className="flex-1 py-2 bg-blue-50 text-blue-700 rounded-xl font-medium hover:bg-blue-100"
              >
                👥 Find Donors
              </button>
            </div>
            
            {/* Interested Donors Section */}
            {donorResponses[req.id]?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setExpandedRequests(prev => ({ ...prev, [req.id]: !prev[req.id] }))}
                  className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                >
                  <span>{expandedRequests[req.id] ? '▼' : '▶'}</span>
                  <span>🙋 {donorResponses[req.id].length} donor(s) interested</span>
                </button>
                
                {expandedRequests[req.id] && (
                  <div className="mt-3 space-y-2">
                    {donorResponses[req.id].map((response) => (
                      <div key={response.id} className="bg-green-50 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-900">{response.responder_name}</p>
                            <p className="text-gray-600">📞 {response.responder_phone}</p>
                            {response.responder_email && (
                              <p className="text-gray-600">✉️ {response.responder_email}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <a
                              href={`tel:${response.responder_phone}`}
                              className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600"
                            >
                              📞 Call
                            </a>
                            <a
                              href={`https://wa.me/91${response.responder_phone?.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600"
                            >
                              📱 WhatsApp
                            </a>
                          </div>
                        </div>
                        {response.message && (
                          <p className="mt-2 text-gray-700 italic">"{response.message}"</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Responded {new Date(response.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {!accessibleRequests?.length && (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <div className="text-6xl mb-4">🩸</div>
          <p className="text-gray-500">No blood requests yet</p>
        </div>
      )}
    </div>
  );

  // Render More Menu
  const renderMore = () => (
    <div className="space-y-4 pb-20 md:pb-0">
      <h2 className="text-xl font-bold text-gray-900">More Options</h2>
      <div className="grid gap-3">
        {[
          { label: 'Analytics', icon: '📊', action: () => setActiveTab('analytics') },
          { label: 'Generate QR Codes', icon: '📱', action: () => setActiveTab('qr') },
          { label: 'Send Congratulations', icon: '🎉', action: () => {} },
          { label: 'Admin Management', icon: '👑', action: () => setActiveTab('admins'), hidden: !isSuperAdminUser },
          { label: 'My Profile', icon: '👤', action: () => setShowProfileModal(true) },
          { label: 'Settings', icon: '⚙️', action: () => {} },
          { label: 'Logout', icon: '🚪', action: () => {
            localStorage.clear();
            window.location.href = '/login';
          }, danger: true }
        ].filter(b => !b.hidden).map((item, i) => (
          <button
            key={i}
            onClick={item.action}
            className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-all ${
              item.danger 
                ? 'bg-amber-50 text-red-800 hover:bg-amber-100' 
                : 'bg-white border border-gray-100 hover:shadow-md'
            }`}
          >
            <span className="text-2xl">{item.icon}</span>
            <span className="font-medium">{item.label}</span>
            <span className="ml-auto text-gray-400">›</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{background: '#f9efd5'}}>
      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-30" style={{background: 'rgba(92,58,30,0.97)', borderBottom: '2px solid rgba(196,69,26,0.6)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold" style={{color: '#f2ddb0'}}>🩸 OCYM Admin</h1>
              <nav className="flex gap-1 ml-8">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'donors', label: 'Donors' },
                  { id: 'requests', label: 'Requests' },
                  { id: 'analytics', label: '📊 Analytics' },
                  { id: 'qr', label: '📱 QR Codes' },
                  { id: 'admins', label: 'Admins', hidden: !isSuperAdminUser }
                ].filter(t => !t.hidden).map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                    style={activeTab === tab.id
                      ? {background: 'rgba(196,69,26,0.3)', color: '#f9efd5'}
                      : {color: 'rgba(249,239,213,0.75)'}}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
                style={{background: 'rgba(196,69,26,0.15)'}}
              >
                <span className="text-sm" style={{color: 'rgba(242,221,176,0.9)'}}>{currentUser?.email}</span>
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{background: '#c4451a', color: 'white'}}>
                  {currentUser?.email?.[0]?.toUpperCase()}
                </div>
              </button>
              <button
                onClick={() => { navigate('/'); onLogout?.(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={{background: 'rgba(196,69,26,0.2)', color: 'rgba(242,221,176,0.9)'}}
                title="Sign out"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-30 px-4 py-3" style={{background: 'rgba(92,58,30,0.97)', borderBottom: '2px solid rgba(196,69,26,0.6)'}}>
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-bold" style={{color: '#f2ddb0'}}>🩸 OCYM Admin</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { navigate('/'); onLogout?.(); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium"
              style={{background: 'rgba(196,69,26,0.3)', color: 'rgba(242,221,176,0.9)'}}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
              </svg>
              Sign Out
            </button>
            <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{background: '#c4451a', color: 'white'}}>
              {currentUser?.email?.[0]?.toUpperCase()}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
        activeTab === 'donors' ? 'py-4 overflow-hidden' : 'py-6'
      }`}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'donors' && renderDonors()}
        {activeTab === 'requests' && renderRequests()}
        {activeTab === 'analytics' && (
          <div className="pb-20 md:pb-0">
            <AdminAnalytics
              donors={accessibleDonors || []}
              requests={accessibleRequests || []}
              admins={admins || []}
              donationHistory={accessibleDonationHistory || []}
            />
          </div>
        )}
        {activeTab === 'admins' && isSuperAdminUser && (
          <div className="pb-20 md:pb-0">
            <AdminManagement 
              currentAdmin={{ email: currentUser?.email, role: 'SUPER_ADMIN', permissions: [] }}
              admins={admins || []}
              onAdminCreated={refreshAdmins}
              onAdminDeleted={refreshAdmins}
            />
          </div>
        )}
        {activeTab === 'qr' && (
          <div className="pb-20 md:pb-0">
            <AdminQRGenerator currentAdmin={{ email: currentUser?.email, role: isSuperAdminUser ? 'SUPER_ADMIN' : 'ADMIN' }} />
          </div>
        )}
        {activeTab === 'more' && renderMore()}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
        isSuperAdmin={isSuperAdminUser}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog isOpen={isOpen} onClose={handleClose} {...config} />

      {/* My Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">👤 My Profile</h3>
              <button onClick={() => setShowProfileModal(false)} className="text-2xl">×</button>
            </div>
            <div className="p-6 space-y-6">
              {(() => {
                const profile = getProfileInfo();
                return (
                  <>
                    {/* User Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl" style={{background: 'linear-gradient(135deg, #8f2a0e, #c4451a)'}}>
                        {currentUser?.email?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">{currentUser?.name || currentUser?.email}</h4>
                        <p className="text-gray-500">{currentUser?.email}</p>
                      </div>
                    </div>

                    {/* Role Badge */}
                    <div className="rounded-xl p-4" style={{background: 'linear-gradient(135deg, #f9efd5, #f2ddb0)', border: '1px solid #c8924a'}}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{isSuperAdminUser ? '👑' : '👤'}</span>
                        <span className="font-bold" style={{color: '#8f2a0e'}}>{profile.role}</span>
                      </div>
                      <p className="text-sm" style={{color: '#5c3a1e'}}>{profile.description}</p>
                    </div>

                    {/* Allowed Locations */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">📍 Allowed Locations</h5>
                      <div className="flex flex-wrap gap-2">
                        {profile.isLocationRestricted ? (
                          profile.locations.length > 0 ? (
                            profile.locations.map(loc => (
                              <span key={loc} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                {loc}
                              </span>
                            ))
                          ) : (
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">
                              No locations assigned
                            </span>
                          )
                        ) : (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                            🌐 All Locations (State-wide Access)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Permissions */}
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">🔐 Permissions ({profile.permissions.length})</h5>
                      <div className="bg-gray-50 rounded-xl p-4 max-h-48 overflow-y-auto">
                        <ul className="space-y-2">
                          {profile.permissions.map((permission, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {permission}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Account Info */}
                    <div className="border-t pt-4">
                      <h5 className="font-semibold text-gray-900 mb-2">ℹ️ Account Info</h5>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-600">Status: <span className="text-green-600 font-medium">Active</span></p>
                        <p className="text-gray-600">Role: <span className="font-medium">{currentUser?.role || 'SUPER_ADMIN'}</span></p>
                        {currentUser?.last_login && (
                          <p className="text-gray-600">Last Login: <span className="font-medium">{new Date(currentUser.last_login).toLocaleString()}</span></p>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowProfileModal(false)}
                className="w-full py-2 text-white rounded-lg font-medium transition-colors" style={{background: '#c4451a'}}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Donor Modal */}
      {showAddDonorModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddDonorModal(false)}>
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">➕ Add New Donor</h3>
              <button onClick={() => setShowAddDonorModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 text-xl leading-none">×</button>
            </div>
            <div className="[&_.min-h-screen]:min-h-0 [&_.bg-hero]:hidden [&_.section-container]:px-0 [&_.py-12]:py-4">
              <DonorRegistration
                onSuccess={(donorId) => {
                  setShowAddDonorModal(false);
                  refreshDonors();
                  toast.success('Donor added successfully!');
                }}
                onCancel={() => setShowAddDonorModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Request Modal */}
      {showAddRequestModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAddRequestModal(false)}>
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg">➕ Add New Request</h3>
              <button onClick={() => setShowAddRequestModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 text-xl leading-none">×</button>
            </div>
            <div className="[&_.min-h-screen]:min-h-0 [&_.bg-hero]:hidden [&_.section-container]:px-0 [&_.py-12]:py-4">
              <BloodRequestForm
                onSuccess={(request) => {
                  setShowAddRequestModal(false);
                  refreshRequests();
                  toast.success('Blood request created successfully!');
                }}
                onCancel={() => setShowAddRequestModal(false)}
                requestedBy={currentUser?.email || currentUser?.id || 'admin'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Promote Modal */}
      {showPromoteModal && selectedDonor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPromoteModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-gray-900">⭐ Promote to Admin</h3>
                <p className="text-sm text-gray-500 mt-0.5">Assign a role and access permissions</p>
              </div>
              <button onClick={() => setShowPromoteModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">×</button>
            </div>

            <div className="p-5 space-y-5">
              {/* Donor summary */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center font-bold text-red-700 text-sm">{selectedDonor.blood_group}</div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedDonor.name}</p>
                  <p className="text-xs text-gray-500">{selectedDonor.email || selectedDonor.phone_number} · {selectedDonor.district}</p>
                </div>
              </div>

              {/* Role selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Role</label>
                <div className="space-y-2">
                  {[['ADMIN', '🔑 Admin', 'Full access — all donors, requests, analytics. Cannot remove Super Admin.'],
                    ['DISTRICT_ADMIN', '📍 District Admin', 'Access limited to assigned district(s) only.']
                  ].map(([value, label, desc]) => (
                    <label key={value} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      promoteRole === value ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input type="radio" name="role" value={value} checked={promoteRole === value}
                        onChange={() => setPromoteRole(value)} className="mt-0.5 accent-purple-600" />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* District selector — only for District Admin */}
              {ADMIN_ROLES[promoteRole]?.locationRestricted && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Assigned Districts <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Select one or more districts this admin can manage.</p>
                  <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {DISTRICTS_KERALA.map(d => (
                      <label key={d} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-all ${
                        promoteDistricts.includes(d)
                          ? 'border-purple-400 bg-purple-50 text-purple-800 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}>
                        <input type="checkbox" checked={promoteDistricts.includes(d)}
                          onChange={() => setPromoteDistricts(prev =>
                            prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
                          )}
                          className="accent-purple-600"
                        />
                        {d}
                      </label>
                    ))}
                  </div>
                  {promoteDistricts.length > 0 && (
                    <p className="text-xs text-purple-600 mt-2 font-medium">✓ {promoteDistricts.length} district{promoteDistricts.length > 1 ? 's' : ''} selected</p>
                  )}
                </div>
              )}

              {/* Permissions preview */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                <p className="font-semibold mb-1">⚠️ This action will:</p>
                <ul className="space-y-0.5 list-disc list-inside">
                  <li>Create an admin account with the email <strong>{selectedDonor.email || '(no email on file)'}</strong></li>
                  <li>Grant <strong>{ADMIN_ROLES[promoteRole]?.name}</strong> permissions immediately</li>
                  {ADMIN_ROLES[promoteRole]?.locationRestricted && <li>Restrict access to: <strong>{promoteDistricts.join(', ') || 'none selected'}</strong></li>}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t bg-gray-50 rounded-b-2xl flex gap-3">
              <button onClick={() => setShowPromoteModal(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={handleCreateAdmin} disabled={promoteLoading || (ADMIN_ROLES[promoteRole]?.locationRestricted && promoteDistricts.length === 0)}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {promoteLoading ? <span className="animate-spin">⏳</span> : '⭐'}
                {promoteLoading ? 'Promoting…' : `Promote to ${ADMIN_ROLES[promoteRole]?.name}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Donation Modal */}
      {showRecordDonationModal && selectedDonor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-4 border-b">
              <h3 className="font-bold text-lg">🩸 Record Blood Donation</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">Donor</p>
                <p className="font-medium text-gray-900">{selectedDonor.name}</p>
                <p className="text-sm text-gray-500">{selectedDonor.blood_group} • {selectedDonor.district}</p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Donation Date
                </label>
                <input
                  type="date"
                  value={donationDate}
                  onChange={(e) => setDonationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Defaults to today. Select the actual donation date.
                </p>
              </div>

              <div className="rounded-xl p-4 mb-6" style={{background: '#fdf4f0', border: '1px solid #f09a72'}}>
                <p className="text-sm" style={{color: '#8f2a0e'}}>
                  <span className="font-medium">Next eligible donation:</span>{' '}
                  {donationDate && (() => {
                    const [y, m, d] = donationDate.split('-').map(Number);
                    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
                    dt.setUTCDate(dt.getUTCDate() + 90);
                    return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  })()}
                </p>
                <p className="text-xs mt-1" style={{color: '#b83a15'}}>
                  (90 days from donation date as per medical guidelines)
                </p>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowRecordDonationModal(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={submitDonationRecord}
                  className="flex-1 py-3 text-white rounded-xl font-medium" style={{background: '#c4451a'}}
                >
                  Record Donation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardNew;
